(function () {
  "use strict";
  const studio = "https://studiov2.feltright.com";
  const productCache = new Map();
  let variationsPromise;
  async function json(url, options) {
    const response = await fetch(url, options);
    if (!response.ok) {
      const error = new Error("Product information could not load. Please try again.");
      error.status = response.status;
      throw error;
    }
    return response.json();
  }
  function product(handle) {
    if (!productCache.has(handle))
      productCache.set(
        handle,
        json(
          (window.Shopify?.routes?.root || "/") +
            "products/" +
            encodeURIComponent(handle) +
            ".js",
        ).catch((error) => {
          productCache.delete(handle);
          throw error;
        }),
      );
    return productCache.get(handle);
  }
  function init(root) {
    if (root.__wainscotingInitialized) return;
    root.__wainscotingInitialized = true;
    const core = window.WainscotingCore;
    const section = root.closest("product-info");
    const find = (selector) => root.querySelector(selector);
    const cards = [...root.querySelectorAll("[data-design-variant]")];
    const status = find("[data-wainscoting-status]");
    const preview = find("[data-wainscoting-preview-wall]");
    const atc = find("[data-wainscoting-atc]");
    const studioLink = find("[data-studio-link]");
    const palette = find("[data-wainscoting-swatches]");
    const woodPalette = find("[data-wainscoting-wood-swatches]");
    const strip = find("[data-wainscoting-designs]");
    const headerPrice = document.querySelector("[data-wainscoting-header-price]");
    const chips = find("[data-wainscoting-color-chips]");
    const colorMap = JSON.parse(root.dataset.wainscotingColorMap || "{}");
    const isWood = (shape) => core.hasWoodSlats([shape]);
    // Theme-editor setting: button label.
    const atcLabel = atc.dataset.atcLabel || "Add to Cart";
    let variations = [],
      selectedCard,
      colorwaySelection = "default",
      columns = [],
      selected = null,
      // Armed "Select all" chip: { color, tiles } with the tiles that had that color
      // when it was armed. Swatches recolor those tiles and they stay selected.
      paintAll = null,
      customized = false,
      originalColors = [],
      customColors = [],
      colorway = "original",
      quantityMode = "width",
      manualSets = 1,
      showHelp = false,
      request = 0,
      busy = false;
    const money = (cents) =>
      new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: window.Shopify?.currency?.active || "USD",
      }).format(cents / 100);
    function message(text) {
      status.textContent = text;
      status.hidden = !text;
    }
    function quantity() {
      if (quantityMode === "sets")
        return core.quantities(columns, 0, manualSets);
      const feet = Number(find("[data-input-feet]").value),
        inches = Number(find("[data-input-inches]").value);
      if (
        !Number.isFinite(feet) ||
        !Number.isFinite(inches) ||
        feet < 0 ||
        feet > 100 ||
        inches < 0 ||
        inches >= 12
      )
        throw new Error(
          "Enter feet from 0 to 100 and inches from 0 to less than 12.",
        );
      return core.quantities(
        columns,
        feet * 12 + inches,
        null,
      );
    }
    function textureVariation(variant, product) {
      const normalize = (value) => String(value).toLowerCase().replace(/[^a-z0-9]/g, "");
      return variations.find((v) => String(v.variant_id) === String(variant.id)) ||
        variations.find((v) => v.handle === product.handle &&
          normalize(v.label.split("-").slice(1).join("-")) === normalize(variant.option1));
    }
    function metadata(shape, variant, p) {
      const variation = textureVariation(variant, p);
      return {
        ...shape.metadata,
        variant_id: String(variant.id),
        product_id: String(p.id),
        handle: p.handle,
        price: variant.price / 100,
        color_label: variant.option1,
        photo_url:
          variation?.photo || variant.featured_image?.src || p.featured_image,
      };
    }
    function isPurchasable() {
      return columns.length && columns.flat().every((shape) =>
        !shape.product.previewOnly && shape.product.variants.some((variant) =>
          String(variant.id) === shape.metadata.variant_id && variant.available,
        ),
      );
    }
    async function component(handle) {
      try {
        return await product(handle);
      } catch (error) {
        if (error.status !== 404 || handle !== "acoustic-wood-slat-wainscoting-panel")
          throw error;
        // Studio's public catalog can render unreleased panels, but never grants purchase availability.
        const rows = variations.filter((v) => v.handle === handle && !v.archived_at);
        if (!rows.length) throw error;
        return {
          id: rows[0].product_id, handle, title: "4 ft Wood Slat Panel", previewOnly: true,
          variants: rows.map((v) => ({ id: v.variant_id, option1: v.label,
            price: Math.round(Number(v.price) * 100), available: false })),
        };
      }
    }
    async function hydrate(shapes) {
      const products = await Promise.all(
        [...new Set(shapes.map((s) => s.metadata.handle))].map(component),
      );
      return shapes.map((shape) => {
        const p = products.find(
          (p) => String(p.id) === String(shape.metadata.product_id),
        );
        if (!p)
          throw new Error("A tile in this design is no longer available.");
        const variant = p.variants.find(
          (v) => String(v.id) === String(shape.metadata.variant_id),
        );
        if (!variant || !variant.price || (!variant.available && !core.hasWoodSlats([shape])))
          throw new Error(
            "A tile in this design is unavailable. Please choose another design.",
          );
        return { ...shape,
          offsetX: shape.offsetX ?? shape.width / 2,
          offsetY: shape.offsetY ?? (shape.type === "arc" ? 0 : shape.height / 2),
          metadata: metadata(shape, variant, p), product: p };
      });
    }
    function serialize() {
      return core.repeatColumns(
        columns.map((column) => column.map(({ product, ...shape }) => shape)),
      );
    }
    function svgElement(tag, attrs) {
      const element = document.createElementNS(
        "http://www.w3.org/2000/svg",
        tag,
      );
      Object.entries(attrs).forEach(([key, value]) =>
        element.setAttribute(key, value),
      );
      return element;
    }
    const textureLoads = new Map();
    // Tiles draw at under ~100 CSS px, so ask Shopify's CDN for a small rendition (~30KB instead of ~150KB).
    const textureSrc = (url) => /cdn\.shopify\.com|\/cdn\/shop\//.test(url) && !/[?&]width=/.test(url)
      ? url + (url.includes("?") ? "&" : "?") + "width=300" : url;
    function loadTexture(url) {
      if (!textureLoads.has(url)) {
        const source = new Image();
        source.src = textureSrc(url);
        textureLoads.set(url, window.PdpDesignLinks.waitImage(source).then((ready) => {
          if (!ready) throw new Error("Texture image could not load.");
          return { width: source.naturalWidth, height: source.naturalHeight };
        }));
      }
      return textureLoads.get(url);
    }
    function updateTexture(image, shape, initial = false, reportError = true) {
      const url = shape.metadata.photo_url;
      image.__wainscotingTexture = url;
      if (!initial && image.getAttribute("href") === textureSrc(url)) return;
      loadTexture(url).then(({ width, height }) => {
        if (!image.isConnected || image.__wainscotingTexture !== url) return;
        image.setAttribute("href", textureSrc(url));
        image.setAttribute("x", -(shape.offsetX ?? 60));
        image.setAttribute("y", -(shape.offsetY ?? 0));
        image.setAttribute("width", shape.width || 120);
        image.setAttribute("height", shape.height || 60);
        image.setAttribute("transform", `translate(${shape.x} ${shape.y}) rotate(${shape.rotation || 0})`);
        if (shape.type === "arc") {
          const texture = core.arcTexture(width, height, shape.width || 120, shape.height || 60);
          ["x", "y", "width", "height"].forEach((key) => image.setAttribute(key, texture[key]));
          image.setAttribute("transform", `translate(${shape.x} ${shape.y}) rotate(${(shape.rotation || 0) + texture.rotation})`);
        } else if (core.rotateTexture(shape, width, height)) {
          image.setAttribute("x", "0");
          image.setAttribute("y", "0");
          image.setAttribute("width", shape.height);
          image.setAttribute("height", shape.width);
          image.setAttribute("transform", `translate(${shape.x} ${shape.y}) rotate(${shape.rotation || 0}) translate(${shape.width - shape.offsetX} ${-shape.offsetY}) rotate(90)`);
        }
      }).catch(() => {
        textureLoads.delete(url);
        if (reportError && image.isConnected && image.__wainscotingTexture === url)
          message("That color's preview image could not load. Please try another color.");
      });
    }
    // The selected tile and an armed chip's tiles are pressed; pdp-studio-selected-tile outlines them.
    const pressed = (c, t, original) => (selected?.c === c && selected?.t === t) || !!paintAll?.tiles.has(original);
    function renderCanvas() {
      const shapes = core.repeatColumns(columns),
        b = core.bounds(shapes);
      const geometry = JSON.stringify([showHelp, shapes.map((s) =>
        [s.type, s.x, s.y, s.width, s.height, s.offsetX, s.offsetY, s.rotation])]);
      const mounted = preview.firstElementChild;
      if (mounted && preview.__wainscotingGeometry === geometry) {
        let index = 0;
        columns.forEach((column, c) => column.forEach((original, t) => {
          const shape = shapes[index++];
          const group = mounted.querySelector(`[data-tile-column="${c}"][data-tile-index="${t}"]`);
          group.setAttribute("aria-pressed", String(pressed(c, t, original)));
          group.setAttribute("aria-label", `Column ${c + 1}, ${original.metadata.color_label} ${original.product.title}`);
          group.setAttribute("tabindex", busy ? "-1" : "0");
          updateTexture(group.querySelector("image"), shape);
        }));
        return;
      }
      preview.__wainscotingGeometry = geometry;
      // Room on the top and left for the Figma dimension brackets (units are 1/10 inch).
      const pad = Math.max(b.width, b.height) * 0.12;
      const helpRoom = showHelp ? pad * 0.9 : 0;
      const svg = svgElement("svg", {
        viewBox: `${b.x - pad * 1.6} ${b.y - pad * 1.4 - helpRoom} ${b.width + pad * 2} ${b.height + pad * 1.8 + helpRoom}`,
        width: "100%",
        role: "group",
        "aria-label": "Your wainscoting set",
      });
      svg.style.maxHeight = "260px";
      const label = (text, x, y, rotate) => {
        const t = svgElement("text", {
          x, y, "text-anchor": "middle", fill: "#6D6D6D",
          "font-family": "Montserrat, sans-serif", "font-weight": "300",
          "font-size": pad * 0.42,
          ...(rotate ? { transform: `rotate(-90 ${x} ${y})` } : {}),
        });
        t.textContent = text;
        return t;
      };
      const inches = (units) => `${+(units / 10).toFixed(2)}″`;
      const bracket = (d) => svgElement("path", { d, fill: "none", stroke: "#6D6D6D", "stroke-width": "0.67", "vector-effect": "non-scaling-stroke" });
      const tick = pad * 0.18, top = b.y - pad * 0.45, left = b.x - pad * 0.45;
      svg.append(
        bracket(`M${b.x} ${top + tick} V${top} H${b.x + b.width} V${top + tick}`),
        label(inches(b.width), b.x + b.width / 2, top - pad * 0.22),
        bracket(`M${left + tick} ${b.y} H${left} V${b.y + b.height} H${left + tick}`),
        label(inches(b.height), left - pad * 0.3, b.y + b.height / 2, true),
      );
      let offset = 0;
      columns.forEach((column, c) =>
        column.forEach((original, t) => {
          const shape = shapes[offset++],
            box = core.box(shape);
          const group = svgElement("g", {
            role: "button",
            tabindex: busy ? "-1" : "0",
            "data-tile-column": c,
            "data-tile-index": t,
            "aria-pressed": String(pressed(c, t, original)),
            "aria-label": `Column ${c + 1}, ${original.metadata.color_label} ${original.product.title}`,
          });
          const image = svgElement("image", {
            href: textureSrc(shape.metadata.photo_url),
            width: shape.width || 120,
            height: shape.height || 60,
            x: -(shape.offsetX ?? 60),
            y: -(shape.offsetY ?? 0),
            preserveAspectRatio: "none",
            transform: `translate(${shape.x} ${shape.y}) rotate(${shape.rotation || 0})`,
          });
          if (shape.type === "arc") {
            image.setAttribute("x", String(-(shape.width || 120) / 2));
            image.setAttribute("y", String(-(shape.height || 60)));
            image.setAttribute(
              "transform",
              `translate(${shape.x} ${shape.y}) rotate(${(shape.rotation || 0) + 180})`,
            );
          }
          // Decode a new texture before replacing an already visible tile.
          updateTexture(image, shape, true);
          group.append(image);
          group.append(
            svgElement("rect", {
              x: box.x,
              y: box.y,
              width: box.width,
              height: box.height,
              fill: "transparent",
              stroke: "#1C1C1E",
              "stroke-width": "0.75",
              "vector-effect": "non-scaling-stroke",
              class: "pdp-studio-tile-outline",
            }),
          );
          const select = () => {
            if (!busy) {
              paintAll = null;
              // Clicking the selected tile again returns to painting the whole set.
              selected = selected?.c === c && selected?.t === t ? null : { c, t };
              render();
            }
          };
          group.addEventListener("click", select);
          group.addEventListener("keydown", (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              select();
              preview
                .querySelector(
                  `[data-tile-column="${c}"][data-tile-index="${t}"]`,
                )
                ?.focus({ preventScroll: true });
            }
          });
          svg.append(group);
        }),
      );
      if (showHelp && columns.length) {
        const first = core.bounds(shapes.slice(0, columns[0].length));
        svg.append(svgElement("rect", {
          x: first.x, y: first.y, width: first.width, height: first.height,
          fill: "none", stroke: "#3B9BFF", "stroke-width": "4", "vector-effect": "non-scaling-stroke",
        }));
        // Figma's "Single Column" tag: two short lines centered over the first column.
        const size = pad * 0.27, tagW = size * 5.6, tagH = size * 2.9;
        // Sits above the width label so the measurement stays readable.
        const cx = first.x + first.width / 2, tagY = first.y - pad * 1.12 - tagH;
        svg.append(svgElement("rect", { x: cx - tagW / 2, y: tagY, width: tagW, height: tagH, fill: "#75B0EE", stroke: "#3B9BFF", "stroke-width": "3", "vector-effect": "non-scaling-stroke" }));
        ["Single", "Column"].forEach((word, i) => {
          const tag = label(word, cx, tagY + size * (1.25 + i * 1.1));
          tag.setAttribute("font-size", size);
          tag.setAttribute("fill", "#fff");
          tag.setAttribute("font-weight", "700");
          svg.append(tag);
        });
      }
      preview.replaceChildren(svg);
    }
    // Figma palette order: neutrals, greens, blues, then warm and bright colors.
    const colorOrder = ["Ebony", "Cast", "Mineral", "Armor", "Fog", "Nickel", "Zinc", "Cocoa", "Timber", "Ash", "Cashmere", "Porcelain", "Ivory", "Latte",
      "Atlantic", "Palm", "Mint", "Kiwi", "Aloe", "Olive", "Sage", "Oxford", "Admiral", "Indigo", "Slate Blue", "Cayman", "Periwinkle", "Powder Blue",
      "Crimson", "Wine", "Moab", "Cinnamon", "Aries", "Mustard", "Dandelion", "Scarlet", "Lavender", "Wisteria", "Plum", "Hot Pink", "Coral", "Lilac"];
    const sortColors = (variants) => [...variants].sort((a, b) => {
      const rank = (v) => (colorOrder.indexOf(v.option1) + 1) || colorOrder.length + 1;
      return rank(a) - rank(b);
    });
    // The variant a tile would take for a color, only if it can actually be priced and shown.
    const usable = (target, color) => target.product.variants.find((v) =>
      v.option1 === color && v.price && (v.available || target.product.previewOnly));
    function swatch(variant, shape, active, onPick, unusable, onWarm) {
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "wainscoting-swatch-btn tw-relative tw-h-8 tw-w-8 tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-grey-medium disabled:tw-opacity-30 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-navy";
      button.dataset.colorName = variant.option1;
      button.title =
        variant.option1 + (shape.product.previewOnly ? " — Preview only" : variant.available ? "" : " — Sold out");
      button.setAttribute("aria-label", button.title);
      button.setAttribute("aria-pressed", String(active));
      button.classList.toggle("is-active", active);
      button.disabled = busy || unusable;
      const variation = textureVariation(variant, shape.product);
      const blank = variations.find((v) => v.handle === "square-tile" &&
        v.label.replace(/[^a-z0-9]/gi, "").toLowerCase() ===
        (variant.option1 + "Blank").replace(/[^a-z0-9]/gi, "").toLowerCase());
      const photo = isWood(shape) ? variation?.photo : blank?.photo;
      if (photo) {
        const img = document.createElement("img");
        img.src = photo;
        img.alt = "";
        img.className = "tw-h-full tw-w-full tw-rounded-full tw-object-cover";
        img.loading = "lazy";
        button.append(img);
      } else button.style.backgroundColor = "#ddd";
      // Start the texture download on intent so the tile can swap as soon as it is clicked.
      const warm = () => onWarm?.(variant.option1);
      button.addEventListener("pointerenter", warm);
      button.addEventListener("focus", warm);
      button.addEventListener("click", () => {
        onPick(variant.option1);
        message("");
        render();
        (isWood(shape) ? woodPalette : palette)
          .querySelector(`[data-color-name="${CSS.escape(variant.option1)}"]`)
          ?.focus({ preventScroll: true });
      });
      return button;
    }
    // A swatch of this material recolors the armed color's tiles, else the selected tile, else every tile.
    function targets(wood) {
      if (paintAll)
        return columns.flat().filter((s) => isWood(s) === wood && paintAll.tiles.has(s));
      const shape = selected && columns[selected.c]?.[selected.t];
      return shape && isWood(shape) === wood ? [shape] : columns.flat().filter((s) => isWood(s) === wood);
    }
    function paint(wood, color) {
      colorwaySelection = "custom";
      customized = true;
      colorway = "custom";
      const tiles = targets(wood);
      tiles.forEach((target) => {
        const variant = usable(target, color);
        if (variant) target.metadata = metadata(target, variant, target.product);
      });
      // A swatch of the other material recolors nothing and disarms the chip.
      if (paintAll) paintAll = tiles.length ? { ...paintAll, color } : null;
    }
    // Preload the textures paint() would apply for a color.
    function warm(wood, color) {
      targets(wood).forEach((target) => {
        const variant = usable(target, color);
        if (variant) loadTexture(metadata(target, variant, target.product).photo_url).catch(() => {});
      });
    }
    function renderPalettes() {
      const shape = selected && columns[selected.c]?.[selected.t];
      find("[data-selection-target]").textContent = shape
        ? `Column ${selected.c + 1}: ${shape.metadata.color_label} ${shape.product.title} selected`
        : "No tile selected";
      find("[data-selection-hint]").textContent = shape
        ? `The ${isWood(shape) ? "wood" : "color"} swatches now change this tile only. Select it again to change the whole set.`
        : "Swatches color the whole set. Click a tile to color just that tile.";
      const hasFelt = columns.flat().some((s) => !isWood(s));
      const chipsHeading = find(hasFelt ? "[data-felt-panel] > h2" : "[data-wood-panel] > h3");
      // Re-inserting the row on every render would blur the focused chip.
      if (chipsHeading.nextElementSibling !== chips) chipsHeading.after(chips);
      window.PdpPaintAll.render(chips, columns.flat().map((s) => s.metadata.color_label), paintAll?.color, (color) => {
        const photo = columns.flat().find((s) => s.metadata.color_label === color)?.metadata.photo_url;
        return colorMap[color] || (photo ? `center/cover url("${textureSrc(photo)}")` : "#ddd");
      });
      [[false, palette, "[data-selected-color-name]"], [true, woodPalette, "[data-selected-wood-name]"]]
        .forEach(([wood, container, nameSelector]) => {
          const group = columns.flat().filter((s) => isWood(s) === wood);
          const tiles = targets(wood);
          // An armed chip shows its color as the current one, like a selected tile.
          const source = paintAll ? tiles[0] || group[0] : shape && isWood(shape) === wood ? shape : group[0];
          // Wood-only designs promote the wood picker to step 2, as in Figma Design 17.
          if (wood) find("[data-wood-label]").textContent =
            columns.flat().some((s) => !isWood(s)) ? "Wood:" : "2. Wood:";
          const panel = find(wood ? "[data-wood-panel]" : "[data-felt-panel]");
          panel.classList.toggle("tw-hidden", !group.length);
          panel.classList.toggle("tw-flex", !!group.length);
          const current = source?.metadata.color_label;
          const uniform = !!current && (source === shape || current === paintAll?.color ||
            group.every((s) => s.metadata.color_label === current));
          find(nameSelector).textContent = uniform ? current : group.length ? "Mixed" : "";
          if (!source) {
            container.replaceChildren();
            container.__wainscotingPalette = null;
            return;
          }
          const variants = sortColors(source.product.variants);
          const key = JSON.stringify([source.product.id, variants.map((v) => [v.id, v.option1, v.available, v.price])]);
          if (container.__wainscotingPalette !== key) {
            container.replaceChildren(...variants.map((variant) => swatch(
              variant, source, false, (color) => paint(wood, color), false, (color) => warm(wood, color),
            )));
            container.__wainscotingPalette = key;
          }
          // Preload every color the swatches can apply so a click swaps from cache, not the CDN.
          const warmKey = JSON.stringify([key, tiles.map((t) => t.product.id)]);
          if (container.__wainscotingWarm !== warmKey) {
            container.__wainscotingWarm = warmKey;
            (window.requestIdleCallback || setTimeout)(() => variants.forEach((v) => warm(wood, v.option1)));
          }
          variants.forEach((variant, index) => {
            const button = container.children[index];
            const active = uniform && variant.option1 === current;
            button.setAttribute("aria-pressed", String(active));
            button.classList.toggle("is-active", active);
            // Painting a group needs the color on every target tile's own product.
            button.disabled = busy || tiles.some((target) => !usable(target, variant.option1));
          });
        });
    }
    const customEdits = new WeakMap();
    function rememberCustomColors() {
      if (selectedCard && columns.length && colorway === "custom")
        customEdits.set(selectedCard, JSON.parse(JSON.stringify(columns)));
    }
    function renderColorways(preload = false) {
      rememberCustomColors();
      const colorBackground = (colors) => window.blocksStudioSwatchBackground(
        [...new Set(colors.filter(Boolean))], (name) => colorMap[name] || "#ddd",
      );
      cards.forEach((card) => {
        const frame = card.closest("[data-wainscoting-design-card]") || card.closest(".wainscoting-design-card-frame") || card;
        const slot = frame.querySelector("[data-design-colorways]");
        if (!slot) return;
        const presetButtons = [...slot.querySelectorAll("[data-wainscoting-colorway]")];
        const active = card === selectedCard && columns.length;
        // The row is always reserved so selecting a card never changes its size.
        slot.classList.toggle("tw-invisible", !active);
        slot.setAttribute("aria-hidden", String(!active));
        slot.querySelectorAll("button").forEach((button) => {
          button.disabled = busy || !active;
          button.tabIndex = active ? 0 : -1;
        });
        if (!active && !preload) return;
        if (presetButtons.length) {
          presetButtons.forEach((button) => {
            const key = button.dataset.wainscotingColorway;
            const source = key === "default" ? card : button;
            // Optional presets are visible only after their layout and colors validate.
            button.classList.toggle("tw-hidden", !!source.dataset.studioColorwayBaseId &&
              (!!source._wainscotingSwatchError || !source._wainscotingColors?.length));
            const pressed = key === colorwaySelection;
            button.setAttribute("aria-pressed", String(pressed));
            ["tw-outline", "tw-outline-2", "tw-outline-offset-2", "tw-outline-navy"].forEach((name) => button.classList.toggle(name, pressed));
            button.disabled = busy || !active || (key !== "custom" && (!source.dataset.designStudioId || source.dataset.designAvailable !== "true")) ||
              !!source._wainscotingSwatchError || (!!source.dataset.studioColorwayBaseId && !source._wainscotingColors);
            if (key === "custom") {
              const saved = customEdits.get(card);
              if (saved) {
                const metadata = saved.flat().map((shape) => shape.metadata);
                const colors = [...new Set(metadata.map((m) => m.color_label))];
                button.dataset.colors = JSON.stringify(colors);
                button.title = `Custom colors: ${colors.join(", ")}`;
                button.setAttribute("aria-label", button.title);
                button.replaceChildren();
                button.style.background = colorBackground(colors);
              }
              return;
            }
            button.replaceChildren();
            const swatchColors = [];
            let colors = source._wainscotingColors;
            if (!colors && source.dataset.designLayout) {
              try {
                colors = JSON.parse(source.dataset.designLayout)?.map((row) => {
                  const id = String(row.variant_id).split("/").pop();
                  const variation = variations.find((v) => String(v.variant_id) === id);
                  return variation?.label.split("-").slice(1).join("-").trim();
                });
              } catch (_) {}
            }
            button.style.background = window.blocksStudioColorwaySwatchBackground(
              { colors: colors || [], swatchColors }, (name) => colorMap[name] || "#ddd",
            );
            if (colors?.length) {
              button.title = "Colors: " + [...new Set(colors)].join(", ");
              button.setAttribute("aria-label", button.title);
            }
            if (!colors?.length && !swatchColors.length && source.dataset.designStudioId &&
                source.dataset.designAvailable === "true" && !source._wainscotingSwatchPending && !source._wainscotingSwatchError) {
              source._wainscotingSwatchPending = true;
              loadShapes(source).then((shapes) => {
                source._wainscotingColors = shapes.map((shape) => shape.metadata.color_label);
                if (root.isConnected) renderColorways();
              }).catch((error) => {
                source._wainscotingSwatchError = error.message;
                source._wainscotingSwatchRetryable = error.retryable === true;
                button.disabled = true;
                button.title = error.message;
                button.setAttribute("aria-label", "Colorway unavailable: " + error.message);
                const editorStatus = card.closest("[data-wainscoting-design-card]")?.querySelector("[data-studio-colorway-editor-status]");
                if (editorStatus) editorStatus.textContent = error.message;
              }).finally(() => { source._wainscotingSwatchPending = false; });
            }
          });
          return;
        }
        if (!active) return;
        if (colorway === "custom")
          customColors = columns.map((column) => column.map((shape) => ({ ...shape.metadata })));
        ["original", "custom"].forEach((mode) => {
          let button = slot.querySelector(`[data-colorway="${mode}"]`);
          if (!button) {
            button = document.createElement("button");
            button.type = "button";
            button.dataset.colorway = mode;
            button.className = "wainscoting-colorway tw-h-[23px] tw-w-[23px] tw-rounded-full tw-border tw-border-solid tw-border-grey-medium focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-navy";
            if (mode === "custom") button.style.borderStyle = "dashed";
            button.addEventListener("click", (event) => {
              event.stopPropagation();
              if (busy || card !== selectedCard) return;
              customized = true;
              colorway = mode;
              columns.forEach((column, c) => column.forEach((shape, t) => {
                const saved = mode === "original" ? originalColors[t] :
                  (customColors[c] || customColors[customColors.length - 1])?.[t];
                if (saved) shape.metadata = { ...saved };
              }));
              message("");
              render();
            });
            slot.append(button);
          }
          const metadata = mode === "original" ? originalColors : customColors.flat();
          const colors = [...new Set(metadata.map((m) => m.color_label))];
          button.dataset.colors = JSON.stringify(colors);
          button.title = `${mode === "original" ? "Original colors" : "Custom colors"}: ${colors.join(", ")}`;
          button.setAttribute("aria-label", button.title);
          button.setAttribute("aria-pressed", String(colorway === mode));
          button.classList.toggle("is-active", colorway === mode);
          button.disabled = busy;
          button.tabIndex = 0;
          button.style.background = colorBackground(colors);
        });
      });
    }
    function syncStrip() {
      const max = strip.scrollWidth - strip.clientWidth - 2;
      [["prev", strip.scrollLeft > 2], ["next", strip.scrollLeft < max]].forEach(([dir, more]) => {
        find(`[data-design-scroll="${dir}"]`).disabled = !more;
        find(`[data-design-fade="${dir}"]`).style.opacity = more ? "1" : "0";
      });
    }
    function render() {
      if (!columns.length) return;
      // A colorway or Studio reload replaces the tiles, which drops the armed selection.
      if (paintAll && !columns.flat().some((s) => paintAll.tiles.has(s))) paintAll = null;
      renderCanvas();
      renderPalettes();
      renderColorways();
      find("[data-wainscoting-trimming]").hidden = !core.hasWoodSlats(columns.flat());
      find("[data-wainscoting-unavailable]").hidden = !!isPurchasable();
      if (!busy) atc.textContent = isPurchasable() ? atcLabel : "Not available yet";
      find("[data-column-count-display]").textContent = columns.length;
      find('[data-column-count-stepper="remove"]').disabled =
        busy || columns.length <= 1;
      find('[data-column-count-stepper="add"]').disabled =
        busy || columns.length >= 6;
      root
        .querySelectorAll(
          "[data-quantity-mode], [data-stepper], [data-input-feet], [data-input-inches]",
        )
        .forEach((el) => (el.disabled = busy));
      find('[data-stepper="minus"]').disabled = busy || manualSets <= 1;
      cards.forEach((card) => {
        card.disabled =
          busy ||
          !card.dataset.designStudioId ||
          card.dataset.designAvailable !== "true";
      });
      studioLink.setAttribute("aria-disabled", String(busy));
      try {
        const q = quantity();
        const tiles = core.cartTiles(serialize());
        const cents =
          Math.round(
            Object.values(tiles).reduce((sum, tile) => sum + tile.cost, 0) *
              100,
          ) * q.sets;
        if (headerPrice) headerPrice.textContent = money(cents / q.sets);
        find("[data-wainscoting-subtotal-price]").textContent = money(cents);
        // Wood designs are ordered by column (Figma Design 17/18); felt designs by set.
        const byColumn = core.hasWoodSlats(columns.flat());
        root.querySelectorAll("[data-count-noun]").forEach((el) => (el.textContent = byColumn ? "Column" : "Set"));
        const unit = find("[data-count-unit]");
        if (byColumn) unit.textContent = "Columns";
        else if (unit.textContent === "Columns") unit.innerHTML = 'Sets<span class="tw-hidden lg:tw-inline"> of Columns</span>';
        find("[data-count-heading]").textContent = byColumn ? "Number of Columns" : "Number of Sets of Columns";
        find("[data-quantity-helper]").textContent = quantityMode === "width"
          ? "Enter the width you want to cover."
          : byColumn ? "Enter the number of columns you want." : "Enter the number of sets of custom columns you want.";
        find("[data-calculated-sets-count]").textContent = byColumn ? q.totalColumns : q.sets;
        find("[data-stepper-value]").textContent = byColumn ? q.totalColumns : q.sets;
        find("[data-wainscoting-coverage]").textContent =
          `${q.sets} set${q.sets === 1 ? "" : "s"} × ${columns.length} column${columns.length === 1 ? "" : "s"} = ${q.totalColumns} columns. Covers ${(q.coverage / 12).toFixed(2).replace(/\.00$/, "")} ft (${q.coverage.toFixed(2).replace(/\.00$/, "")} in).`;
        const included = find("[data-wainscoting-included]");
        const template = find("[data-wainscoting-included-template]").firstElementChild;
        included.replaceChildren();
        Object.entries(tiles).forEach(([id, tile]) => {
          const shape = columns
            .flat()
            .find((s) => s.metadata.variant_id === id);
          const row = template.cloneNode(true);
          const img = row.querySelector("[data-included-image]");
          if (shape.metadata.photo_url) img.src = shape.metadata.photo_url;
          else img.remove();
          row.querySelector("[data-included-quantity]").textContent = tile.quantity * q.sets;
          row.querySelector("[data-included-name]").textContent =
            `${shape.metadata.color_label} ${shape.product.title}`;
          included.append(row);
        });
        atc.disabled = busy || !isPurchasable();
      } catch (error) {
        message(error.message);
        atc.disabled = true;
      }
    }
    const shapeLoads = new WeakMap();
    function loadShapes(source) {
      if (!shapeLoads.has(source)) {
        const retryable = error => ["TypeError", "AbortError"].includes(error.name) ||
          [408, 429].includes(error.status) || error.status >= 500;
        const load = async () => {
          const layout = JSON.parse(source.dataset.designLayout || "null");
          // Fetch the selected design alongside the catalog, then validate with both.
          const catalog = ensureVariations();
          const [design] = await Promise.all([
            source.dataset.studioColorwayBaseId
              ? window.PdpStudioColorways.load(source.dataset.studioColorwayBaseId, source.dataset.designStudioId, studio, { repeat: true, variations: catalog })
              : json(studio + "/api/designs/" + encodeURIComponent(source.dataset.designStudioId), { cache: "no-store" }),
            catalog,
          ]);
          let shapes = core.fromStudio(design, variations);
          const entries = Array.isArray(layout) ? layout.map((row) => {
            const variantId = String(row.variant_id).split("/").pop();
            const variation = variations.find((v) => String(v.variant_id) === variantId);
            return { ...row, variant_id: variantId, handle: row.handle || variation?.handle,
              product_id: String(row.product_id || variation?.product_id).split("/").pop(),
              count: row.count ?? row.configurations?.length };
          }) : [];
          if (entries.some((row) => row.configurations?.length) && !entries.some((row) => row.handle === "acoustic-wood-slat-wainscoting-panel"))
            shapes = core.fromLayout(entries, variations, shapes, { preserveSavedColors: true });
          return hydrate(shapes);
        };
        const pending = load().catch(async (error) => {
          if (!retryable(error)) throw error;
          // One retry shares the pending cache entry; validation errors never retry.
          await new Promise(resolve => setTimeout(resolve, 500));
          return load();
        }).catch((error) => {
          shapeLoads.delete(source);
          error.retryable = retryable(error);
          throw error;
        });
        shapeLoads.set(source, pending);
      }
      return shapeLoads.get(source);
    }
    async function renderThumbnail(card) {
      const target = card.querySelector("[data-wainscoting-thumbnail]");
      if (!target || card.disabled || !card.dataset.designStudioId) return;
      try {
        const shapes = await loadShapes(card);
        if (!target.isConnected || !shapes.length) return;
        const b = core.bounds(shapes), pad = 4;
        const svg = svgElement("svg", {
          viewBox: `${b.x - pad} ${b.y - pad} ${b.width + pad * 2} ${b.height + pad * 2}`,
          width: "100%", height: "87", "aria-hidden": "true",
        });
        shapes.forEach((shape) => {
          const image = svgElement("image", {
            href: textureSrc(shape.metadata.photo_url),
            x: -shape.offsetX, y: -shape.offsetY,
            width: shape.width, height: shape.height,
            preserveAspectRatio: "none",
            transform: `translate(${shape.x} ${shape.y}) rotate(${shape.rotation || 0})`,
          });
          svg.append(image);
          updateTexture(image, shape, true, false);
        });
        target.replaceChildren(svg);
      } catch (_) {
        // Keep the saved thumbnail or photo if the live layout cannot load.
      }
    }
    function loadVisibleThumbnails() {
      if (!window.IntersectionObserver) {
        cards.forEach(renderThumbnail);
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          renderThumbnail(entry.target);
        });
      }, { root: strip, rootMargin: "128px" });
      cards.filter((card) => card.querySelector("[data-wainscoting-thumbnail]")).forEach((card) => observer.observe(card));
    }
    function retryTransientColorways(card) {
      const frame = card.closest("[data-wainscoting-design-card]");
      const sources = [card, ...(frame?.querySelectorAll("[data-studio-colorway-base-id]") || [])];
      sources.forEach(source => {
        if (source._wainscotingSwatchRetryable) {
          delete source._wainscotingSwatchError;
          delete source._wainscotingSwatchRetryable;
        }
      });
      const editorStatus = frame?.querySelector("[data-studio-colorway-editor-status]");
      if (editorStatus) editorStatus.textContent = sources.find(source => source._wainscotingSwatchError)?._wainscotingSwatchError || "";
    }
    // The init-time variations fetch runs once; if it fails, retry on the
    // next selection instead of leaving every design unusable until reload.
    async function ensureVariations() {
      if (variations.length) return variations;
      if (!variationsPromise)
        variationsPromise = json(studio + "/api/variations", { cache: "no-store" }).catch((error) => {
          variationsPromise = null;
          throw error;
        });
      variations = await variationsPromise;
      loadVisibleThumbnails();
      return variations;
    }
    async function choose(card, source = card, presetKey = "default", preserveSet = false) {
      if (busy || card.disabled || source.dataset.designAvailable !== "true" || !source.dataset.designStudioId || (card === selectedCard && columns.length && customized && !preserveSet)) return;
      if (source === card) retryTransientColorways(card);
      rememberCustomColors();
      const previous = preserveSet ? { columns, selected, selectedCard, colorwaySelection, manualSets, customized, originalColors, customColors, colorway } : null;
      const columnCount = preserveSet ? Math.max(1, columns.length) : 1;
      const ticket = ++request;
      section?.setAttribute("data-design-media-pending", "");
      atc.disabled = true;
      columns = [];
      selected = null;
      paintAll = null;
      preview.replaceChildren();
      palette.replaceChildren();
      woodPalette.replaceChildren();
      palette.__wainscotingPalette = null;
      woodPalette.__wainscotingPalette = null;
      renderColorways();
      message("Loading design…");
      find("[data-wainscoting-trimming]").hidden = true;
      find("[data-wainscoting-unavailable]").hidden = true;
      find("[data-wainscoting-coverage]").textContent = "";
      find("[data-wainscoting-included]").replaceChildren();
      if (headerPrice) headerPrice.textContent = "—";
      find("[data-wainscoting-subtotal-price]").textContent = "—";
      // Scroll only the strip; scrollIntoView would also move the page on load.
      const cardLeft = card.offsetLeft - strip.offsetLeft;
      if (cardLeft < strip.scrollLeft || cardLeft + card.offsetWidth > strip.scrollLeft + strip.clientWidth)
        strip.scrollTo({ left: cardLeft - (strip.clientWidth - card.offsetWidth) / 2, behavior: "smooth" });
      cards.forEach((el) => {
        const active = el === card;
        (el.closest("[data-wainscoting-design-card]") || el).classList.toggle("is-active", active);
        el.closest(".wainscoting-design-card-frame")?.classList.toggle("is-active", active);
        el.setAttribute("aria-pressed", String(active));
      });
      // Gallery metadata comes from Liquid; it does not depend on Studio or tile prices.
      let selectionFailed = false, mediaTimer;
      const mediaReady = new Promise(resolve => {
        mediaTimer = setTimeout(() => resolve(false), 12000);
        window.dispatchEvent(new CustomEvent("pdp:selectedVariant", { detail: {
          id: source.dataset.designVariant || source.dataset.designSourceVariant,
          image: source.dataset.designImage,
          coverImage: source.dataset.designCoverImage,
          onReady: resolve,
        } }));
      }).finally(() => clearTimeout(mediaTimer)).then(ready => {
        if (ready && ticket === request && !selectionFailed)
          section?.removeAttribute("data-design-media-pending");
        return ready;
      });
      document.querySelectorAll("[data-pdp-title] .pdp-title-text, [data-pdp-title-mobile] .pdp-title-text").forEach(title => {
        title.textContent = card.dataset.designName;
      });
      try {
        const hydrated = await loadShapes(source);
        if (ticket !== request) return;
        selectedCard = card;
        colorwaySelection = presetKey;
        source._wainscotingColors = hydrated.map((shape) => shape.metadata.color_label);
        columns = Array.from({ length: columnCount }, () => JSON.parse(JSON.stringify(hydrated)));
        customized = false;
        originalColors = hydrated.map((shape) => ({ ...shape.metadata }));
        customColors = columns.map((column) => column.map((shape) => ({ ...shape.metadata })));
        colorway = "original";
        selected = null;
        manualSets = previous ? previous.manualSets : 1;
        const url = new URL(location.href);
        if (source.dataset.designProductHandle) url.searchParams.set("design", source.dataset.designProductHandle);
        if (card.dataset.designKey) url.searchParams.set("wainscoting_design", card.dataset.designKey);
        if (!card.dataset.designKey || card.dataset.designMasterVariant === "true") url.searchParams.set("variant", card.dataset.designVariant);
        else url.searchParams.delete("variant");
        if (presetKey === "default") url.searchParams.delete("wainscoting_colorway");
        else url.searchParams.set("wainscoting_colorway", presetKey);
        history.replaceState(null, "", url);
        message("");
        render();
        renderColorways(true);
        let textureTimer;
        const texturesReady = Promise.race([
          Promise.all(hydrated.map(shape => textureLoads.get(shape.metadata.photo_url))).then(() => true).catch(() => false),
          new Promise(resolve => { textureTimer = setTimeout(() => resolve(false), 12000); }),
        ]).finally(() => clearTimeout(textureTimer));
        const readiness = await Promise.all([mediaReady, texturesReady]);
        const ready = readiness.every(Boolean);
        if (ticket !== request) return;
        window.PdpDesignLinks.finish(section, ready);
        if (!ready) message("We could not load the design photo. Select the design again to retry.");
      } catch (error) {
        selectionFailed = true;
        if (ticket === request) {
          if (previous) {
            ({ columns, selected, selectedCard, colorwaySelection, manualSets, customized, originalColors, customColors, colorway } = previous);
            render();
          }
          message(error.message);
          window.PdpDesignLinks.finish(section, false);
          if (!previous) atc.disabled = true;
        }
      }
    }
    cards.forEach((card) => card.addEventListener("click", () => choose(card)));
    chips.addEventListener("click", (event) => {
      const chip = event.target.closest("[data-paint-all]");
      if (!chip || busy) return;
      const color = chip.dataset.paintAll;
      paintAll = paintAll?.color === color ? null
        : { color, tiles: new Set(columns.flat().filter((s) => s.metadata.color_label === color)) };
      selected = null;
      render();
    });
    root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !paintAll) return;
      paintAll = null;
      render();
    });
    root.querySelectorAll("[data-wainscoting-colorway]").forEach((button) => button.addEventListener("click", () => {
      if (busy || button.disabled) return;
      const card = button.closest("[data-wainscoting-design-card]")?.querySelector("[data-design-key]");
      if (!card || card !== selectedCard || !columns.length) return;
      const key = button.dataset.wainscotingColorway;
      if (key === "custom") {
        const saved = customEdits.get(card);
        if (saved) columns = JSON.parse(JSON.stringify(saved));
        colorwaySelection = "custom";
        customized = true;
        colorway = "custom";
        selected = null;
        paintAll = null;
        message("Click a tile, then choose a color to customize it.");
        render();
      } else choose(card, key === "default" ? card : button, key, true);
    }));
    root
      .querySelectorAll("[data-design-scroll]")
      .forEach((button) =>
        button.addEventListener("click", () => {
          const cards = [...strip.children].filter((card) => card.offsetWidth);
          const nextColumn = cards.find((card) => card.offsetLeft > cards[0].offsetLeft);
          const step = nextColumn ? nextColumn.offsetLeft - cards[0].offsetLeft : strip.clientWidth;
          strip.scrollBy({
            left: (button.dataset.designScroll === "next" ? 1 : -1) * step,
            behavior: "smooth",
          });
        }),
      );
    strip.addEventListener("scroll", syncStrip, { passive: true });
    window.addEventListener("resize", syncStrip);
    syncStrip();
    find("[data-layout-help]").addEventListener("click", (event) => {
      showHelp = !showHelp;
      event.currentTarget.setAttribute("aria-pressed", String(showHelp));
      if (columns.length) renderCanvas();
    });
    root.querySelectorAll("[data-column-count-stepper]").forEach((button) =>
      button.addEventListener("click", () => {
        if (busy || !columns.length) return;
        customized = true;
        colorwaySelection = "custom";
        colorway = "custom";
        if (button.dataset.columnCountStepper === "add" && columns.length < 6)
          columns.push(JSON.parse(JSON.stringify(columns[columns.length - 1])));
        if (
          button.dataset.columnCountStepper === "remove" &&
          columns.length > 1
        )
          columns.pop();
        if (selected && selected.c >= columns.length)
          selected.c = columns.length - 1;
        render();
      }),
    );
    // Shopping mode has a stable URL: ?shop_by=width|sets beside the design
    // keys. Width is the default whenever the parameter is absent or invalid,
    // so deep links can never select an arbitrary quantity mode.
    function applyQuantityMode(mode) {
      quantityMode = mode;
      find("[data-wall-width-panel]").classList.toggle(
        "tw-hidden",
        mode !== "width",
      );
      find("[data-set-count-panel]").classList.toggle(
        "tw-hidden",
        mode !== "sets",
      );
      find("[data-set-count-panel]").classList.toggle(
        "tw-flex",
        mode === "sets",
      );
      root.querySelectorAll("[data-quantity-mode]").forEach((el) => {
        const active = el.dataset.quantityMode === mode;
        el.setAttribute("aria-pressed", String(active));
        el.classList.toggle("tw-bg-navy", active);
        el.classList.toggle("tw-text-white", active);
        el.classList.toggle("tw-font-semibold", active);
        el.classList.toggle("tw-shadow-[0_0.84px_2.51px_rgba(0,0,0,0.08)]", active);
        el.classList.toggle("tw-font-medium", !active);
        el.classList.toggle("tw-text-[#6B6F82]", !active);
      });
    }
    root.querySelectorAll("[data-quantity-mode]").forEach((button) =>
      button.addEventListener("click", () => {
        if (busy) return;
        const mode = button.dataset.quantityMode;
        try {
          manualSets = columns.length ? quantity().sets : 1;
        } catch (_) {
          manualSets = 1;
        }
        if (quantityMode !== mode) customized = true;
        applyQuantityMode(mode);
        const modeUrl = new URL(location.href);
        modeUrl.searchParams.set("shop_by", mode);
        history.replaceState(null, "", modeUrl);
        render();
      }),
    );
    const inboundMode = new URL(location.href).searchParams.get("shop_by");
    if (inboundMode === "width" || inboundMode === "sets")
      applyQuantityMode(inboundMode);
    root.querySelectorAll("[data-stepper]").forEach((button) =>
      button.addEventListener("click", () => {
        customized = true;
        manualSets = Math.max(
          1,
          manualSets + (button.dataset.stepper === "plus" ? 1 : -1),
        );
        render();
      }),
    );
    root
      .querySelectorAll("[data-input-feet], [data-input-inches]")
      .forEach((input) => {
        // Keep page scrolling from stepping a focused native number field.
        input.addEventListener("wheel", () => input.blur(), { passive: true });
        input.addEventListener("input", () => {
          customized = true;
          message("");
          render();
        });
      });
    atc.addEventListener("click", async () => {
      if (busy || !isPurchasable()) return;
      const q = quantity(),
        shapes = serialize();
      busy = true;
      render();
      atc.textContent = "Adding to cart…";
      message("Saving your customized set…");
      try {
        await window.WainscotingCart.add(
          shapes,
          selectedCard.dataset.designName + " Wainscoting",
          q.sets,
        );
        message("Your customized set was added to cart.");
        window.location.assign((window.Shopify?.routes?.root || "/") + "cart");
      } catch (error) {
        message(error.message);
        busy = false;
        atc.textContent = atcLabel;
        render();
      }
    });
    studioLink.addEventListener("click", async (event) => {
      event.preventDefault();
      if (busy || !columns.length) return;
      busy = true;
      render();
      message("Opening your customized set in the Design Studio…");
      try {
        window.location.assign(
          await window.WainscotingCart.openStudio(
            serialize(),
            selectedCard.dataset.designName + " Wainscoting",
          ),
        );
      } catch (error) {
        message(error.message);
        busy = false;
        render();
      }
    });
    function selectInitialDesign() {
      const sources = [...root.querySelectorAll("[data-design-product-handle]")].filter(source => {
        const card = source.matches("[data-design-variant]") ? source : source.closest("[data-wainscoting-design-card]")?.querySelector("[data-design-variant]");
        return card && !card.disabled && source.dataset.designAvailable === "true" && source.dataset.designStudioId && !source.disabled;
      }).map(source => ({ element: source, handle: source.dataset.designProductHandle,
        url: "/products/" + source.dataset.designProductHandle,
        variant: source.dataset.designVariant || source.dataset.designSourceVariant,
        key: source.dataset.designKey }));
      const selectedSource = window.PdpDesignLinks.resolve(sources, { legacyKey: "wainscoting_design" });
      const source = selectedSource?.element;
      const matching = source?.matches("[data-design-variant]") ? source :
        source?.closest("[data-wainscoting-design-card]")?.querySelector("[data-design-variant]");
      const colorway = new URL(location.href).searchParams.get("wainscoting_colorway");
      const preset = matching?.closest("[data-wainscoting-design-card]")?.querySelector(`[data-wainscoting-colorway="${["1", "2", "3"].includes(colorway) ? colorway : "default"}"]`);
      const usablePreset = preset?.dataset.designStudioId && preset.dataset.designAvailable === "true";
      if (matching) {
        const chosen = usablePreset ? preset : source;
        choose(matching, chosen, chosen.dataset.wainscotingColorway || "default");
      } else {
        message("These designs are coming soon.");
        window.PdpDesignLinks.finish(section, false);
      }
    }
    selectInitialDesign();
  }
  function boot(scope) {
    scope.querySelectorAll("[data-wainscoting-configurator]").forEach(init);
  }
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => boot(document));
  else boot(document);
  document.addEventListener("shopify:section:load", (event) =>
    boot(event.target),
  );
})();